import { observer } from 'mobx-react';
import {
  DragDropContext,
  DraggableProvided,
  Droppable,
  DroppableProvided,
} from '@hello-pangea/dnd';
import NonRichtextStringInput from '../../../../../../../Resource/FormTemplates/Inputs/NonRichtextStringInput';
import WithField from '../../../../../../../Resource/FormTemplates/Inputs/WithField';
import { T, notYetTranslatedText } from '../../../../../../../Translations';
import Accordion from '../../../../../../Accordion';
import CarouselDisplayTypeChoice from '../../../../../../Cutesies/CarouselDisplayTypeChoice';
import DragIndicatorIcon from '../../../../../../Cutesies/DragIndicatorIcon';
import ListDisplayTypeChoice from '../../../../../../Cutesies/ListDisplayTypeChoice';
import { LearningDesignSection, sectionContext } from '../../../../Types';
import LearningCollectionEditStore from '../../../Store';
import DeletedExperiences from './DeletedExperiences';
import Experiences from './Experiences';
import GridDisplayTypeChoice from '../../../../../../Cutesies/GridDisplayTypeChoice';
import NoExperiencesIcon from './NoExperiencesIcon';
import DisplayTypeChoice from '../../../../LearningDesigns/Components/DisplayTypeChoice';
import { ProductCollectionDisplayType } from '../../../../../Types';
import { fieldValue, findField } from '../../../../../../../Resource/FormTemplates/Types';

interface Props {
  disabled: boolean;
  store: LearningCollectionEditStore;
  section: LearningDesignSection;
  provided: DraggableProvided;
}

const isDisplayTypeChecked = (
  displayType: ProductCollectionDisplayType,
  section: LearningDesignSection,
) => {
  return fieldValue('display_type', 'string', section.template)
    .map((value) => value === displayType)
    .getOrElseValue(false);
};

const handleDisplayTypeClick = (
  displayType: ProductCollectionDisplayType,
  section: LearningDesignSection,
  setLearningDesignDisplayType: (
    section: LearningDesignSection,
    displayType: ProductCollectionDisplayType,
  ) => void,
) => {
  findField('display_type', 'string', section.template.fields).do(
    (field) => (field.value = displayType),
  );
  setLearningDesignDisplayType(section, displayType);
};

function LearningDesignAccordion({ disabled, store, section, provided }: Props) {
  return (
    <div ref={provided.innerRef} {...provided.draggableProps} className="border-b">
      <Accordion
        titleIcon={
          <div className="inline-block cursor-grab" {...provided.dragHandleProps}>
            <DragIndicatorIcon />
          </div>
        }
        title={section.resource.payload.name}
        headerInfo={
          <div className="flex gap-1">
            <NoExperiencesIcon experienceCount={section.resource.payload.experiences.length} />
          </div>
        }
      >
        <div className="border border-gray-200 bg-gray-50 px-7 py-7">
          <WithField kind="string" name="name" fields={section.template.fields}>
            {(field) => <NonRichtextStringInput field={field} disabled={disabled} />}
          </WithField>
        </div>
        <div className="border border-gray-200 bg-gray-50 px-7 py-7">
          <WithField kind="string" name="description" fields={section.template.fields}>
            {(field) => <NonRichtextStringInput field={field} disabled={disabled} />}
          </WithField>
        </div>
        <div className="border border-gray-200 bg-gray-50 px-7 py-7">
          <div className="flex flex-row mb-4 text-sm font-semibold">
            <span>
              <T kind="Display Type" />
            </span>
          </div>
          <div className="flex flex-row flex-wrap gap-y-3 gap-x-4">
            <DisplayTypeChoice
              label={notYetTranslatedText('Carousel')}
              onClick={() =>
                handleDisplayTypeClick('carousel', section, store.setLearningDesignDisplayType)
              }
              isChecked={isDisplayTypeChecked('carousel', section)}
              icon={<CarouselDisplayTypeChoice />}
              section={section}
            />
            <DisplayTypeChoice
              label={notYetTranslatedText('List')}
              onClick={() =>
                handleDisplayTypeClick('list', section, store.setLearningDesignDisplayType)
              }
              isChecked={isDisplayTypeChecked('list', section)}
              icon={<ListDisplayTypeChoice />}
              section={section}
            />
            <DisplayTypeChoice
              label={notYetTranslatedText('Grid')}
              onClick={() =>
                handleDisplayTypeClick('grid', section, store.setLearningDesignDisplayType)
              }
              isChecked={isDisplayTypeChecked('grid', section)}
              icon={<GridDisplayTypeChoice />}
              section={section}
            />
          </div>
        </div>
        <div className="border border-gray-200 bg-gray-50 px-7 py-7">
          <WithField kind="string" name="display_button_label" fields={section.template.fields}>
            {(field) => <NonRichtextStringInput field={field} disabled={disabled} />}
          </WithField>
        </div>
        <div className="border border-gray-200 bg-gray-50 px-7 py-7">
          <p className="mb-4 text-sm font-semibold">
            <T kind="Programs" />
          </p>
          <table className="w-full">
            <DragDropContext onDragEnd={store.handleOnDragEndExperience(section)}>
              <Droppable droppableId="characters">
                {(provided: DroppableProvided) => {
                  return (
                    <tbody
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="border-0"
                    >
                      <Experiences context={sectionContext(section)} store={store} />
                      {provided.placeholder}
                    </tbody>
                  );
                }}
              </Droppable>
            </DragDropContext>
            <tbody>
              <DeletedExperiences
                context={sectionContext(section)}
                learningCollectionStore={store}
              />
            </tbody>
          </table>
        </div>
      </Accordion>
    </div>
  );
}

export default observer(LearningDesignAccordion);
